import { useCallback, useEffect, useState } from "react";
import { apiClient } from "../services/apiClient";
import { Card } from "./common/Card";
import { Button } from "./common/Button";
import { Badge } from "./common/Badge";

/**
 * The register of what is in preview (#378): since when, against which
 * mobile build, how many people have tried it, and whether it may leave.
 *
 * "May leave" needs the minimum days AND the minimum number of distinct
 * people who started a flow named after the feature. Days alone would be a
 * rubber stamp. The server decides it (GET /management/preview); this only
 * shows it and puts features in or takes them out.
 */
export function PreviewRegisterCard({ features }) {
  const [rows, setRows] = useState(null);
  const [pick, setPick] = useState("");
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    apiClient.get("/management/preview")
      .then((r) => { setRows(r.data.preview); setError(null); })
      .catch(() => setError("Could not load the preview register."));
  }, []);
  useEffect(() => { load(); }, [load]);

  async function setState(name, inPreview) {
    try {
      await apiClient.put(`/management/preview/${encodeURIComponent(name)}`, { in_preview: inPreview });
      load();
    } catch {
      setError("Could not change that feature.");
    }
  }

  const inPreview = new Set((rows || []).map((r) => r.feature_name));
  const candidates = (features || []).filter((f) => !inPreview.has(f.feature_name));

  return (
    <Card title="Preview (early access)">
      {error && <p role="alert">{error}</p>}
      {rows && rows.length === 0 && (
        <p style={{ marginTop: 0 }}>
          Nothing is in preview, so users see no Early Access setting. Putting a feature here
          hides it from everyone who has not opted in; it never widens a plan.
        </p>
      )}
      {rows && rows.length > 0 && (
        <table>
          <thead>
            <tr>
              <th>Feature</th><th>Since</th><th>Days</th><th>Tried</th><th>Feedback</th><th>Build</th><th>Leave?</th><th />
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.feature_name}>
                <td>{r.feature_name} {r.preview_writes && <Badge tone="warning">writes data</Badge>}</td>
                <td>{String(r.preview_since).slice(0, 10)}</td>
                <td>{r.days_in_preview} / {r.preview_min_days}</td>
                <td>{r.accounts_tried} / {r.preview_min_accounts}</td>
                <td>{r.feedback_count}</td>
                <td>{r.preview_first_build || "--"}</td>
                <td>{r.ready_to_leave ? <Badge tone="success">Ready</Badge> : <Badge tone="neutral">Not yet</Badge>}</td>
                <td><Button size="sm" variant="secondary" onClick={() => setState(r.feature_name, false)}>Take out of preview</Button></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
        <select id="preview-pick" name="preview-pick" aria-label="Feature to put in preview" value={pick} onChange={(e) => setPick(e.target.value)}>
          <option value="">Choose a feature…</option>
          {candidates.map((f) => <option key={f.feature_name} value={f.feature_name}>{f.feature_name}</option>)}
        </select>
        <Button size="sm" disabled={!pick} onClick={() => { setState(pick, true); setPick(""); }}>Put in preview</Button>
      </div>
    </Card>
  );
}
